import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { authAPI, twoFactorAPI } from '../services/api';
import { socketService } from '../services/socketService';
import toast from 'react-hot-toast';

// Types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: 'BUYER' | 'SELLER' | 'ADMIN';
  isActive: boolean;
  twoFactorEnabled?: boolean;
  createdAt: string;
  seller?: {
    id: string;
    businessName: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    commission: number;
  };
}

export interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: 'BUYER' | 'SELLER';
  businessName?: string;
  acceptTerms?: boolean;
}

// Action Types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'CLEAR_ERROR' };

// Initial State
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  loading: false,
  error: null,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Context
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  clearError: () => void;
  checkAuth: () => Promise<void>;
  validate2FA: (tempToken: string, code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check authentication
  const checkAuth = useCallback(async (): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await authAPI.getProfile();

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: response.data.data.user,
          token: state.token!,
        },
      });
    } catch (error: any) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error.response?.data?.message || 'Error de autenticación',
      });
    }
  }, [state.token]);

  // Login
  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await authAPI.login(credentials);
      const responseData = response.data.data;

      // Handle 2FA requirement
      if (responseData.requires2FA) {
        dispatch({ type: 'AUTH_FAILURE', payload: '' });
        // Store temp token for 2FA validation — caller handles UI
        (window as any).__2faTempToken = responseData.tempToken;
        throw Object.assign(new Error('2FA_REQUIRED'), { requires2FA: true, tempToken: responseData.tempToken });
      }

      const { user, accessToken, refreshToken } = responseData;
      const token = accessToken || responseData.token;

      localStorage.setItem('token', token);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

      dispatch({ type: 'AUTH_SUCCESS', payload: { user, token } });
      toast.success(`¡Bienvenido, ${user.firstName}!`);
    } catch (error: any) {
      if (error.requires2FA) throw error;
      const errorMessage = error.response?.data?.message || 'Error al iniciar sesión';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  };

  // Register
  const register = async (data: RegisterData): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await authAPI.register(data);
      const responseData = response.data.data;
      const { user } = responseData;
      const token = responseData.accessToken || responseData.token;
      const refreshToken = responseData.refreshToken;

      localStorage.setItem('token', token);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

      dispatch({ type: 'AUTH_SUCCESS', payload: { user, token } });

      if (data.role === 'SELLER') {
        toast.success('Cuenta creada. Tu solicitud de vendedor está en revisión.');
      } else {
        toast.success(`¡Bienvenido, ${user.firstName}!`);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error al crear cuenta';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  };

  // Validate 2FA code during login
  const validate2FA = async (tempToken: string, code: string): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await twoFactorAPI.validate(tempToken, code);
      const { user, accessToken, refreshToken } = response.data.data;

      localStorage.setItem('token', accessToken);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

      dispatch({ type: 'AUTH_SUCCESS', payload: { user, token: accessToken } });
      toast.success(`¡Bienvenido, ${user.firstName}!`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Código 2FA inválido';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  };

  // Logout
  const logout = (): void => {
    try {
      authAPI.logout().catch(console.error);
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      socketService.disconnect();
      dispatch({ type: 'LOGOUT' });
      toast.success('Sesión cerrada correctamente');
    }
  };

  // Update user
  const updateUser = (user: User): void => {
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  // Clear error
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Check authentication status on mount
  useEffect(() => {
    if (state.token) {
      checkAuth();
    }
  }, [state.token, checkAuth]);

  // Setup socket connection when user is authenticated
  useEffect(() => {
    if (state.user && state.token) {
      socketService.connect(state.token);
    } else {
      socketService.disconnect();
    }

    return () => {
      socketService.disconnect();
    };
  }, [state.user, state.token]);

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    clearError,
    checkAuth,
    validate2FA,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;